// ============================================================================
//  AuthLayout.tsx — NexusCorp ERP · Premium auth layout 2026
//  Split layout · Dark #160C26 brand panel · Modern glassmorphism
// ============================================================================
import { Outlet } from 'react-router-dom'
import { BrandMark } from './BrandMark'

export default function AuthLayout() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', background: 'var(--bg)' }}>

      {/* ── Left panel — brand showcase ── */}
      <div
        className="hidden lg:flex"
        style={{
          width: '44%', maxWidth: 620,
          position: 'relative',
          overflow: 'hidden',
          flexDirection: 'column',
          justifyContent: 'space-between',
          padding: '48px 56px',
          background: '#1E1B3A',
        }}
      >
        {/* Ambient glows */}
        <div style={{
          position: 'absolute', top: -100, right: -100, width: 450, height: 450,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(124,58,237,0.12) 0%, transparent 65%)',
          pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', bottom: -80, left: -60, width: 400, height: 400,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(100,69,146,0.10) 0%, transparent 65%)',
          pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', top: '45%', left: '55%', width: 200, height: 200,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(124,58,237,0.06) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />

        {/* Subtle grid */}
        <svg style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%',
          opacity: 0.03, pointerEvents: 'none',
        }} aria-hidden="true">
          <defs>
            <pattern id="auth-grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#auth-grid)" />
        </svg>

        {/* Logo */}
        <div style={{ position: 'relative', zIndex: 2, display: 'flex', alignItems: 'center', gap: 14 }}>
          <BrandMark size={44} radius={13} />
          <div>
            <p style={{
              fontFamily: '"Raleway", sans-serif', fontWeight: 800, fontSize: 20,
              color: '#fff', letterSpacing: '-0.02em', lineHeight: 1.1,
            }}>NexusCorp</p>
            <p style={{
              fontSize: 10, color: 'rgba(255,255,255,0.35)',
              letterSpacing: '0.14em', textTransform: 'uppercase', marginTop: 3,
              fontFamily: '"Raleway", sans-serif', fontWeight: 600,
            }}>Gestion intégrée</p>
          </div>
        </div>

        {/* Hero */}
        <div style={{ position: 'relative', zIndex: 2 }}>
          <h1 style={{
            fontFamily: '"Raleway", sans-serif', fontWeight: 900, fontSize: 42,
            color: '#fff', lineHeight: 1.1, letterSpacing: '-0.035em', marginBottom: 20,
          }}>
            Votre entreprise,<br />
            <span style={{
              background: 'linear-gradient(90deg, var(--brand-orange), var(--brand-amber))',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
            }}>
              pilotée au<br />même endroit
            </span>
          </h1>
          <p style={{
            fontSize: 15, fontFamily: '"Raleway", sans-serif', fontWeight: 400,
            color: 'rgba(255,255,255,0.40)', lineHeight: 1.8, maxWidth: 340, marginBottom: 32,
          }}>
            ERP intégré — comptabilité, facturation, RH &amp; paie, CRM, trésorerie et dépenses, réunis en un seul outil.
          </p>

          {/* Feature pills */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {['Comptabilité', 'Facturation', 'RH & Paie', 'CRM'].map(b => (
              <span key={b} style={{
                fontSize: 11, fontFamily: '"Raleway", sans-serif', fontWeight: 600,
                padding: '5px 14px', borderRadius: 99,
                background: 'rgba(124,58,237,0.08)',
                border: '1px solid rgba(124,58,237,0.15)',
                color: 'rgba(255,255,255,0.60)',
                letterSpacing: '0.01em',
                backdropFilter: 'blur(4px)',
              }}>
                {b}
              </span>
            ))}
          </div>
        </div>

        {/* Footer */}
        <p style={{
          position: 'relative', zIndex: 2,
          fontSize: 11, color: 'rgba(255,255,255,0.15)',
          fontFamily: '"Raleway", sans-serif',
        }}>
          © 2026 Nexus Corporate · Dakar, Sénégal
        </p>
      </div>

      {/* ── Right panel — form area ── */}
      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: '40px 24px',
        background: 'var(--bg)',
        position: 'relative',
      }}>
        {/* Subtle top gradient on mobile */}
        <div className="lg:hidden" style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: 4,
          background: 'linear-gradient(90deg, var(--brand-orange), var(--brand-amber))',
        }} />

        {/* Mobile logo */}
        <div className="lg:hidden" style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          gap: 8, marginBottom: 40,
        }}>
          <BrandMark size={48} radius={14} />
          <div style={{ textAlign: 'center' }}>
            <p style={{
              fontFamily: '"Raleway", sans-serif', fontWeight: 800, fontSize: 20,
              color: 'var(--tx)', letterSpacing: '-0.02em',
            }}>NexusCorp</p>
            <p style={{
              fontSize: 12, color: 'var(--tx3)', marginTop: 2,
              fontFamily: '"Raleway", sans-serif', fontWeight: 500,
            }}>Gestion intégrée</p>
          </div>
        </div>

        {/* Form card */}
        <div style={{
          width: '100%', maxWidth: 440,
          background: '#fff', borderRadius: 20,
          padding: '40px 36px',
          border: '1px solid var(--bd)',
          boxShadow: '0 1px 3px rgba(15,12,29,0.03), 0 8px 30px rgba(15,12,29,0.06), 0 20px 60px rgba(15,12,29,0.04)',
        }}>
          <Outlet />
        </div>

        <p style={{
          marginTop: 28, fontSize: 11.5, color: 'var(--tx4)',
          fontFamily: '"Raleway", sans-serif',
        }}>
          © 2026 NexusCorp · Tous droits réservés
        </p>
      </div>
    </div>
  )
}
